import {
  IMcpFerramenta,
  IMcpJsonSchema,
  McpEstado,
  McpResultado,
  McpTipoFerramenta,
} from '../../hooks/useMcp';

// Funções puras da tela de MCP (sem React), para ficarem testáveis.

export const ROTULO_TIPO: Record<McpTipoFerramenta, string> = {
  leitura: 'Leitura',
  proposta: 'Proposta (não grava)',
  aplicacao: 'Aplicação (grava)',
};

export type Tom = 'info' | 'aviso' | 'sucesso' | 'neutro' | 'perigo';

// Tons das etiquetas da Central (ver pages/permissoes/ui.tsx).
export const TOM_TIPO: Record<McpTipoFerramenta, Tom> = {
  leitura: 'sucesso',
  proposta: 'aviso',
  aplicacao: 'perigo',
};

export const ROTULO_ESTADO: Record<McpEstado, string> = {
  habilitada: 'Habilitada',
  desabilitada: 'Desabilitada',
  removida: 'Removida',
};

export const TOM_ESTADO: Record<McpEstado, Tom> = {
  habilitada: 'sucesso',
  desabilitada: 'aviso',
  removida: 'neutro',
};

export const TOM_RESULTADO: Record<McpResultado, Tom> = {
  ok: 'sucesso',
  negado: 'aviso',
  erro: 'perigo',
};

export const ROTULO_RESULTADO: Record<McpResultado, string> = {
  ok: 'OK',
  negado: 'Negado',
  erro: 'Erro',
};

export function iniciais(texto: string | null | undefined): string {
  const partes = (texto ?? '').trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  return (partes[0][0] + (partes.length > 1 ? partes[partes.length - 1][0] : '')).toUpperCase();
}

// "04/11/2026" e "10:13" separados, para agrupar a atividade por dia como os Logs de acesso.
export function diaDe(iso: string | null | undefined): string {
  if (!iso) return 'Sem data';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return 'Sem data';
  return data.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });
}

export function horaDe(iso: string | null | undefined): string {
  if (!iso) return '--:--';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '--:--';
  return data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export function formatarDataHora(iso: string | null | undefined): string {
  if (!iso) return '-';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '-';
  return data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' });
}

export function formatarBytes(bytes: number): string {
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

function valorDeExemplo(schema: IMcpJsonSchema | undefined): unknown {
  if (!schema) return null;
  if (schema.default !== undefined) return schema.default;
  switch (schema.type) {
    case 'integer':
    case 'number':
      return 1;
    case 'boolean':
      return false;
    case 'array':
      return [valorDeExemplo(schema.items)];
    case 'object':
      return objetoDeExemplo(schema);
    case 'string':
      return 'texto';
    default:
      // anyOf (campo opcional): usa a primeira alternativa que não é null
      if (Array.isArray(schema.anyOf)) {
        const alternativa = (schema.anyOf as IMcpJsonSchema[]).find(a => a.type !== 'null');
        return valorDeExemplo(alternativa);
      }
      return null;
  }
}

function objetoDeExemplo(schema: IMcpJsonSchema): Record<string, unknown> {
  const exemplo: Record<string, unknown> = {};
  const obrigatorios = schema.required ?? [];
  Object.entries(schema.properties ?? {}).forEach(([nome, sub]) => {
    if (obrigatorios.includes(nome)) exemplo[nome] = valorDeExemplo(sub);
  });
  return exemplo;
}

// Corpo JSON-RPC de um `tools/call` com os argumentos obrigatórios preenchidos.
export function exemploDeChamada(ferramenta: IMcpFerramenta): string {
  const corpo = {
    jsonrpc: '2.0',
    id: 1,
    method: 'tools/call',
    params: { name: ferramenta.nome, arguments: objetoDeExemplo(ferramenta.entrada_schema) },
  };
  return JSON.stringify(corpo, null, 2);
}

// Frase que o backend repete no fim de toda descrição de ferramenta (aviso de que o texto
// do banco é dado). Na lista ela só polui; no detalhe expandido continua inteira.
const MARCA_AVISO = ' Textos retornados vêm do banco';

export function resumoDaDescricao(descricao: string): string {
  const posicao = descricao.indexOf(MARCA_AVISO);
  return (posicao >= 0 ? descricao.slice(0, posicao) : descricao).trim();
}

// Endereço de POST do MCP: a base da API (variável de build) + o caminho do servidor.
// Sem base configurada, cai na origem da página.
export function urlDoMcp(baseApi: string | undefined, caminho: string): string {
  const base = (baseApi || window.location.origin).replace(/\/+$/, '');
  return `${base}${caminho.startsWith('/') ? caminho : `/${caminho}`}`;
}

// Mensagem que a API devolveu (campo `detail`) num erro de requisição, se houver.
export function detalheDoErro(erro: unknown): string | undefined {
  const detalhe = (erro as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detalhe === 'string' ? detalhe : undefined;
}

export function resumirAlvo(alvoTipo: string | null, alvoId: number | null): string {
  if (!alvoTipo) return '-';
  return alvoId != null ? `${alvoTipo} #${alvoId}` : alvoTipo;
}

export function jsonLegivel(valor: unknown): string {
  if (valor === null || valor === undefined) return '';
  if (typeof valor === 'string') return valor;
  return JSON.stringify(valor, null, 2);
}

// Campos do schema de entrada para a tabela de argumentos (nome, tipo, obrigatório).
export function argumentosDe(
  schema: IMcpJsonSchema,
): { nome: string; tipo: string; obrigatorio: boolean }[] {
  const obrigatorios = schema.required ?? [];
  return Object.entries(schema.properties ?? {}).map(([nome, sub]) => {
    let tipo = sub.type ?? '';
    if (!tipo && Array.isArray(sub.anyOf)) {
      tipo = (sub.anyOf as IMcpJsonSchema[])
        .map(a => a.type)
        .filter(t => t && t !== 'null')
        .join(' | ');
    }
    if (tipo === 'array' && sub.items?.type) tipo = `lista de ${sub.items.type}`;
    return { nome, tipo: tipo || 'objeto', obrigatorio: obrigatorios.includes(nome) };
  });
}
